import { connect } from "react-redux";
import _ from "lodash";
import { useRouter } from "@lib/i18n/navigation-pages-router";
import { useState } from "react";
import { useTranslations } from "next-intl";
import Input from "@components/core/Input";
import Button from "@components/core/Button";
import Link from "@components/core/Link";
import * as authActions from "@actions/auth-actions";
import ahoy from "@lib/ahoy";
import { analytics } from "@lib/trackers/analytics";
import { authTypes } from "@utils/constants/authTypes";
import Facebook from "./facebook";
import Apple from "./apple";
import GoogleLogin from "./google";
import { isStringNotEmpty, isStringMinLengthReached, isEmailValid } from "@utils/validations";

const Auth = ({
  signIn,
  signUp,
  initType,
  userAction,
  onAfterAuth = () => {},
  withGoBackButton = false,
}) => {
  const t = useTranslations("common");

  const router = useRouter();

  const [state, setState] = useState({ loading: false, error: false });
  const [type, setType] = useState(initType);
  const [formData, setFormData] = useState({
    email: router.query?.email || "",
    password: router.query?.pwd || "",
  });
  const [errors, setErrors] = useState({
    email: null,
    password: null,
  });

  function switchType(newType) {
    setType(newType);
    setState({ loading: false, error: false });
  }

  function authSuccess(user, authBy) {
    analytics.identify(user.uid, { email: user.email });

    onAfterAuth(type, authBy, user);
  }

  function authError(err, authBy) {
    const { status, data } = err.response || {};
    if (status === 422 && _.find(data.errors.email, { error: "taken" })) {
      const errorButton = (
        <Button
          variant="link-primary"
          className="text-danger mb-4"
          onClick={() => switchType("signin")}
        >
          {t("auth.email_taken")}
        </Button>
      );

      setState({
        loading: false,
        error: errorButton,
      });
    } else {
      setState({ loading: false, error: true });
    }

    if (type !== "signin") {
      ahoy("signupfailed", { authBy });
    }
  }

  function isFormDataValid() {
    const { email, password } = formData;
    const errorsTmp = {};

    if (!isStringNotEmpty(email)) {
      errorsTmp.email = t("auth.email_required");
    } else if (!isEmailValid(email)) {
      errorsTmp.email = t("auth.invalid_email");
    }

    if (!isStringNotEmpty(password)) {
      errorsTmp.password = t("auth.password_required");
    } else if (!isStringMinLengthReached(password)) {
      errorsTmp.password = t("auth.error_min_characters");
    }

    setErrors(errorsTmp);

    return Object.keys(errorsTmp).length === 0;
  }

  async function signWithEmail(e) {
    e.preventDefault();

    if (state.loading) return;
    if (!isFormDataValid()) return;

    try {
      setState({ loading: true, error: false });
      const fn = type === "signin" ? signIn : signUp;
      const res = await fn({ user: formData });

      authSuccess(res.user, authTypes.standard);
    } catch (err) {
      authError(err, authTypes.standard);
    }
  }

  const welcomeMessage = () => {
    if (["likeProduct"].includes(userAction)) {
      return t("auth.create_account_to_like_product_title");
    }
    if (type === "signup" && ["addToCart"].includes(userAction)) {
      return t("auth.create_account_to_buy_cart");
    }
    if (type === "signup") return t("auth.welcome");

    return t("auth.nice_to_see_you");
  };

  const displayPageInfo = () => {
    if (["likeProduct"].includes(userAction)) {
      return (
        <p className="text-center mt-4">{t("auth.create_account_to_like_product_subtitle")}</p>
      );
    }
    if (type === "signin") return null;

    return <p className="text-center mt-4">{t("auth.join_community")}</p>;
  };

  const switchTypeLinks = () => {
    if (type === "signin") {
      return (
        <div className="d-flex justify-content-center text-center">
          <span className="my-auto me-1 font-size-sm text-gray">
            <span>{`${t("auth.no_account")} `}</span>
            <span
              variant="link-primary"
              className="link-primary text-primary cursor-pointer"
              style={{ textDecoration: "underline" }}
              onClick={() => switchType("signup")}
            >
              {t("auth.create_account")}
            </span>
          </span>
        </div>
      );
    }

    return (
      <div className="d-flex justify-content-center text-center">
        <span className="my-auto me-1 font-size-sm text-gray">
          <span>{`${t("auth.account_existing")} `}</span>
          <span
            variant="link-primary"
            className="link-primary text-primary cursor-pointer"
            style={{ textDecoration: "underline" }}
            onClick={() => switchType("signin")}
          >
            {t("auth.connect")}
          </span>
        </span>
      </div>
    );
  };

  const displayApiLoginButtons = () => {
    return (
      <section>
        <div className="d-flex gap-2 mt-4 mt-lg-5">
          {process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID && (
            <GoogleLogin
              authSuccess={authSuccess}
              authError={authError}
            />
          )}
          {process.env.NEXT_PUBLIC_FACEBOOK_APP_ID && (
            <Facebook
              authSuccess={authSuccess}
              authError={authError}
            />
          )}
          {process.env.NEXT_PUBLIC_APPLE_CLIENT_ID && (
            <Apple
              authSuccess={authSuccess}
              authError={authError}
            />
          )}
        </div>
      </section>
    );
  };

  const displaySignWithEmailForm = () => {
    return (
      <form onSubmit={signWithEmail}>
        <Input
          name="email"
          label={t("auth.email")}
          type="email"
          value={formData.email}
          error={errors.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="mb-2"
          autoComplete="email"
        />
        <Input
          name="password"
          type="password"
          label={t("auth.password")}
          value={formData.password}
          error={errors.password}
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          autoComplete={type === "signin" ? "current-password" : "new-password"}
        />
        {type === "signin" && (
          <div className="d-flex ms-auto">
            <Link
              href="/forgot-password"
              className="ms-auto link-dark font-size-sm"
            >
              {t("auth.forgot_password")}
            </Link>
          </div>
        )}
        <div className="d-flex w-100 justify-content-center mt-5 mb-3">
          <Button
            variant="primary"
            type="submit"
            loading={state.loading}
          >
            {type === "signin" ? t("auth.connect") : t("auth.register")}
          </Button>
        </div>

        {withGoBackButton && (
          <div className="d-flex w-100 justify-content-center mb-3">
            <Button
              variant="ghost-primary"
              onClick={() => router.back()}
            >
              {t("auth.go_back")}
            </Button>
          </div>
        )}
        {switchTypeLinks()}

        {type === "signup" && (
          <p className="mt-4 mb-0 text-center font-size-xs text-gray">
            {t("auth.marketing_warning")}
          </p>
        )}

        {state.error && (
          <p className="my-3 text-danger text-center">
            {state.error === true ? t("auth.email_password_forgotten") : state.error}
          </p>
        )}
      </form>
    );
  };

  return (
    <div className="container">
      <div className="auth-container">
        <h4 className="text-center mb-0">{welcomeMessage()}</h4>
        <div className="tiers-section">
          {displayPageInfo()}
          {displayApiLoginButtons()}
          <div className="d-flex justify-content-center align-items-center my-4">
            <span className="flex-grow-1 border-bottom border-light my-0" />
            <p className="mx-2 mb-0">{t("auth.or")}</p>
            <span className="flex-grow-1 border-bottom border-light my-0" />
          </div>
        </div>
        <div className="email-section">{displaySignWithEmailForm()}</div>
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  signIn: authActions.signIn,
  signUp: authActions.signUp,
};

export default connect(null, mapDispatchToProps)(Auth);
